// src/views/pages/analytics/Notes.js

import React, { useEffect, useMemo, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CSpinner,
  CFormInput,
  CBadge,
  CButton,
} from "@coreui/react";
import DataTable from "src/components/DataTable";
import { getNotes } from "src/services/Analytics";
import SalesPicker from "src/components/SalesPicker";
import ls from "local-storage";
import { format } from "date-fns";
import { DateRangePicker } from "react-date-range";
import { subMonths } from "date-fns";

function VisitationNotes() {
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: subMonths(new Date(), 1),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const buildFilters = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");
    return {
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
      dateFrom: dateRange?.[0]?.startDate ? format(dateRange[0].startDate, "yyyy-MM-dd") : undefined,
      dateTo: dateRange?.[0]?.endDate ? format(dateRange[0].endDate, "yyyy-MM-dd") : undefined,
    };
  };

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await getNotes(buildFilters());
        // Accept multiple possible shapes: { data: [...] } or { notes: [...] } or array
        const list = res.data.notes || [];
        setNotes(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error fetching visitation notes:", error);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateApply = () => {
    setShowDatePicker(false);
    fetchNotes();
  };

  const rows = useMemo(() => {
    return (notes || []).map((item, index) => {
      const id = item?.id ?? item?.visitationId ?? item?.visitation_id ?? index;
      const date = item?.date ?? item?.visit_date ?? item?.created_at ?? "-";
      const store = item?.storeName ?? item?.store_name ?? item?.store ?? "-";
      const user = item?.userName ?? item?.sales_name ?? item?.user ?? "-";
      const activation = item?.activation ?? "-";
      const note = item?.notes ?? "-";
      const competitor = item?.competitor_activity ?? item?.competitorActivity ?? "-";
      return {
        id,
        date,
        store,
        user,
        notes: note,
        activation,
        competitor_activity: competitor,
      };
    });
  }, [notes]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r.date, r.store, r.user, r.notes, r.activation, r.competitor_activity]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [rows, search]);

  const columns = [
    { field: "date", headerName: "Tanggal", width: 140, valueGetter: (p) => {
      try { return p.row.date ? format(new Date(p.row.date), "yyyy-MM-dd") : "-"; } catch { return p.row.date || "-"; }
    } },
    { field: "store", headerName: "Toko", flex: 1, minWidth: 200 },
    { field: "user", headerName: "Sales", width: 160 },
    { field: "notes", headerName: "Catatan", flex: 1, minWidth: 240 },
    { field: "activation", headerName: "Aktivasi", flex: 1, minWidth: 240 },
    { field: "competitor_activity", headerName: "Aktivitas Kompetitor", flex: 1, minWidth: 240 },
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  return (
    <div>
      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol xs={12} className="mb-2">
              <h4 className="mb-0">Catatan Kunjungan</h4>
            </CCol>
          </CRow>
          <CRow className="align-items-center g-2">
            <CCol xs={12} lg={9}>
              <SalesPicker onFilterChange={fetchNotes} />
            </CCol>
            <CCol xs={12} lg={3}>
              <CFormInput
                type="text"
                placeholder="Cari catatan, toko, atau sales..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </CCol>
          </CRow>
          <CRow className="align-items-center g-2 mt-2">
            <CCol xs={12} lg={9}>
              <CButton color="primary" onClick={() => setShowDatePicker((v) => !v)}>
                {`${format(dateRange[0].startDate, "dd MMM yyyy")} – ${format(dateRange[0].endDate, "dd MMM yyyy")}`}
              </CButton>
            </CCol>
          </CRow>
        </CCardHeader>
        {showDatePicker && (
          <CCardBody>
            <DateRangePicker
              onChange={(item) => setDateRange([item.selection])}
              showSelectionPreview
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={dateRange}
              direction="horizontal"
            />
            <div className="mt-2">
              <CButton color="secondary" className="me-2" onClick={() => setShowDatePicker(false)}>
                Batal
              </CButton>
              <CButton color="primary" onClick={handleDateApply}>
                Terapkan
              </CButton>
            </div>
          </CCardBody>
        )}
        <CCardBody>
          {filteredRows.length ? (
            <DataTable columns={columns} rows={filteredRows} loading={loading} autoHeight density="compact" />
          ) : (
            <div className="text-center text-muted">
              <CBadge color="secondary">Tidak ada catatan kunjungan</CBadge>
            </div>
          )}
        </CCardBody>
      </CCard>
    </div>
  );
}

export default VisitationNotes;
